import { useState } from 'react';

import { Avatar } from '@vk-hr-tek/ui/Avatar';
import { Box } from '@vk-hr-tek/ui/Box';
import { Typography } from '@vk-hr-tek/ui/Typography';
import { Icon } from '@vk-hr-tek/ui/Icon';
import { FormatService } from '@vk-hr-tek/core/format';
import { Link } from '@vk-hr-tek/ui/Link';
import { useInject } from '@vk-hr-tek/core/ioc';
import { useLexicalText } from '@vk-hr-tek/ui/hooks';
import { HTTP_NOT_FOUND_STATUS } from '@vk-hr-tek/core/http/codes';
import { NoImage } from '@vk-hr-tek/app-ui/NoImage';

import { modules } from '@app/modules';

import { PhotoParams, SearchCommentOption } from '../SearchInput.types';
import { useUrlWithPrefix } from '../../../../hooks';

import { SearchOptionMeta } from './SearchOptionMeta';

const getLabel = (
  affiliationName: SearchCommentOption['affiliation']['name'],
) => {
  switch (affiliationName) {
    case 'news':
      return 'Комментарий к новости';
    case 'community_post':
      return 'Комментарий к посту';
    default:
      const exhaustiveCheck: never = affiliationName;

      return exhaustiveCheck;
  }
};

interface SearchOptionCommentProps {
  option: SearchCommentOption;
  loadPhoto: (params: PhotoParams) => Promise<Blob>;
}

export const SearchOptionComment = ({
  option,
  loadPhoto,
}: SearchOptionCommentProps) => {
  const format = useInject(FormatService);
  const [showNoImg, setShowNoImg] = useState(false);

  const handleLoadPhoto = async () => {
    const result = await loadPhoto({
      id: option.user.id,
    }).then(
      (blob) => blob,
      (error) => {
        if (error.status !== HTTP_NOT_FOUND_STATUS) {
          setShowNoImg(true);
        }

        throw error;
      },
    );

    return result;
  };

  const urlWithPrefix = useUrlWithPrefix();
  const getLexicalText = useLexicalText();

  const getHref = (commentOption: SearchCommentOption) => {
    switch (commentOption.affiliation.name) {
      case 'news':
        return `/${modules.news.basename}/${commentOption.affiliation.id}#comment-${commentOption.id}`;
      case 'community_post':
        return `/${modules.community.basename}/${commentOption.affiliation.community_id}/${commentOption.affiliation.community_slug}/post/${commentOption.affiliation.id}#comment-${commentOption.id}`;
      default:
        const exhaustiveCheck: never = commentOption.affiliation.name;

        return exhaustiveCheck;
    }
  };

  const formattedUserShortName = format.toShortName(
    option.user.first_name,
    option.user.middle_name,
    option.user.second_name,
  );

  const labels = [
    { label: formattedUserShortName, isShrink: true },
    { label: getLabel(option.affiliation.name), isShrink: false },
  ];

  return (
    <Box width="100%" p="8">
      <Link
        {...urlWithPrefix(getHref(option))}
        variant="simple"
        display="block"
      >
        <Box width="100%" display="flex" gap="12">
          <Box
            minHeight="40px"
            minWidth="40px"
            height="40px"
            width="40px"
            display="flex"
            gap="12"
            position="relative"
          >
            {showNoImg && <NoImage isCircle />}

            <Box display={showNoImg ? 'none' : 'visible'}>
              <Avatar
                loadPhoto={handleLoadPhoto}
                children={`${option.user.second_name[0]}${option.user.first_name[0]}`}
              />
            </Box>

            <Box
              height={20}
              width={20}
              position="absolute"
              right={-4}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bgcolor="bg.light.primary"
              radius="round"
            >
              <Icon name="comment_alt" size="16" />
            </Box>
          </Box>
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            gap="2"
            maxHeight="60px"
          >
            <SearchOptionMeta labels={labels} date={option.date} />
            <Box>
              <Typography
                variant="body3"
                color="text.light.primary"
                maxLinesCount={2}
                wordBreak="break-all"
              >
                {getLexicalText(option.text) || (
                  <Typography variant="body3" color="text.light.tertirary">
                    *Комментарий без текста*
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Link>
    </Box>
  );
};
